import { DotLegalDatePicker, DotLegalDialog, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { arrow, leftWhiteArrowButton } from "../../common/icons";
import GroupEntityStep from "../../legalEntity/legalEntityAuditTab/groupEntityStep/GroupEntityStep";
import { Box } from "@mui/material";
import { apiUrls } from "../../common/api/apiUrls";
import DotLegalSelectApi from "../../common/components/dotLegalSelectApi/DotLegalSelectApi";
import { QuestionnaireSelectableItem } from "../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import useCreateLegalEntityAuditGroupDialogHooks from "./CreateLegalEntityAuditGroupDialog.hooks";
import { LegalEntityAuditGroupSaveModel } from "./CreateLegalEntityAuditGroupDialog.types";
import TrialUsageWarning from "../../common/components/TrialUsageWarning";
import { useTrialInformationHook } from "../../trial/TrialInformation.hooks";
import MentionInput from "../../common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "../../common/Common.types.ts";
import AuditTemplateLinkWrapper from "../../common/AuditTemplateLinkWrapper.tsx";
import DialogHeaderWithInformation from "@common/components/dialogHeaderWithInformation/DialogHeaderWithInformation";

export interface ICreateAuditGroupDialog {
    auditGroupId?: string;
    auditGroup?: LegalEntityAuditGroupSaveModel;
    auditGroupInProgress?: boolean;
    onDialogClose: () => void;
    onAuditGroupUpdated?: () => void;
}

function CreateLegalEntityAuditGroupDialog(props: ICreateAuditGroupDialog) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const {
        saveModel,
        selectableGroupEntitiesData,
        auditUpdater,
        step,
        showSelectedGroupEntityStep,
        validation,
        selectableResponsiblesData,
        selectableresponsiblesLoading,
        disableOkButton,
        onNextOrSaveClick,
        onPreviousButtonClick,
        isSaving,
        isEdit,
    } = useCreateLegalEntityAuditGroupDialogHooks(props);
    const { hasActiveVendorManagementTrial } = useTrialInformationHook();

    function getDialogContent() {
        if (step === "formData") {
            return formStep;
        }

        return (
            <GroupEntityStep
                showGdpoIfAvailable
                onGroupEntityChanged={auditUpdater.onGroupEntityChanged}
                groupEntityId={saveModel.groupEntityId}
                errormsg={validation.getErrorsForDisplay("groupEntityId")}
                selectableGroupEntitiesData={selectableGroupEntitiesData}
                header={translateString("auditGroupGroupEntityHeader")}
                infoTranslationKey={"auditGroupGroupEntity"}
                checkboxTranslationKey={"auditGroupEntireGroup"}
                checkboxTooltip={translateString("auditGroupGroupEntityTooltip")}
            />
        );
    }

    // The arrow and the right-aligned action row are the stepper affordance, so they belong to the button that
    // moves on to the next step - not to a plain save in a dialog that only ever has the one step.
    const isOnNextStepButton = step === "formData" && showSelectedGroupEntityStep;

    function getButtonText() {
        if (props.auditGroupInProgress) {
            return translateString("save");
        }

        if (step === "formData") {
            return showSelectedGroupEntityStep ? translateString("next") : isEdit ? translateString("save") : translateString("createDraft");
        }

        return isEdit ? translateString("save") : translateString("createDraft");
    }

    const formStep = (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
            <Box sx={{ width: 450 }}>
                <DotLegalTextField
                    errorText={validation.getErrorsForDisplay("name")}
                    label={translateString("name")}
                    value={saveModel.name}
                    debounce={false}
                    onChange={auditUpdater.onAssessmentNameChange}
                    toolTipText={translateString("auditGroupNameTooltip")}
                />

                <MentionInput
                    label={translateString("description")}
                    onChange={auditUpdater.onDescriptionChange}
                    multiline
                    rows={3}
                    value={saveModel.description ?? ""}
                    debounce={false}
                    toolTipText={translateString("auditGroupDescriptionTooltip")}
                    mentionSource={{ entityFromId: "", entityFromType: EntityType.LegalEntityAuditGroup }}
                    manageRelationsExternal
                />

                {!props.auditGroupInProgress && (
                    <AuditTemplateLinkWrapper>
                        <DotLegalSelectApi<QuestionnaireSelectableItem>
                            url={apiUrls().questionnaires.selectableItems}
                            onChange={auditUpdater.onQuestionnaireChange}
                            label={translateString("template")}
                            noOptionsLabel={translateString("noOptions")}
                            selectedItem={saveModel.questionnaireId}
                            toolTipText={translateString("selectAuditInfo")}
                            errorText={validation.getErrorsForDisplay("questionnaireId")}
                        />
                    </AuditTemplateLinkWrapper>
                )}

                <DotLegalSelect
                    selectedItem={saveModel.responsibleId}
                    options={selectableResponsiblesData}
                    isLoading={selectableresponsiblesLoading}
                    onChange={auditUpdater.onResponsibleChanged}
                    label={translateString("responsible")}
                    toolTipText={translateString("auditGroupResponsibleTooltip")}
                    noOptionsLabel={translateString("noOptions")}
                    errorText={validation.getErrorsForDisplay("responsibleId")}
                ></DotLegalSelect>

                <DotLegalDatePicker
                    label={translateString("deadline")}
                    value={saveModel.deadline ?? null}
                    onChange={auditUpdater.onDateChanged}
                    language={getDateTimePickerLanguage()}
                    toolTipText={translateString("auditGroupDeadlineTooltip")}
                    error={!!validation.getErrorsForDisplay("deadline")}
                    helperMessage={validation.getErrorsForDisplay("deadline")}
                />
            </Box>
            {hasActiveVendorManagementTrial && <TrialUsageWarning textKey={"createAuditGroupWarning"} marginTop={2} />}
        </Box>
    );

    return (
        <DotLegalDialog
            buttonOkText={getButtonText()}
            size="md"
            header={
                <DialogHeaderWithInformation
                    header={isEdit || props.auditGroupInProgress ? translateString("editAuditGroup") : translateString("createAuditGroup")}
                    information={isEdit || props.auditGroupInProgress ? undefined : translateString("sendExternalAuditButtonTooltip")}
                />
            }
            open
            buttonOkIcon={isOnNextStepButton ? arrow : undefined}
            previousButtonIcon={leftWhiteArrowButton}
            onDialogClose={props.onDialogClose}
            onOkClick={onNextOrSaveClick}
            okButtonDisabled={disableOkButton}
            previousButtonText={translateString("previous")}
            previousButtonClick={step !== "formData" ? onPreviousButtonClick : undefined}
            buttonRight={showSelectedGroupEntityStep}
            inProgress={isSaving}
        >
            {getDialogContent()}
        </DotLegalDialog>
    );
}

export default CreateLegalEntityAuditGroupDialog;
